import { addToCart, getPhotocards } from '@/apis/main';
import useApi from '@/hooks/useApi';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PhotocardItem from '../PhotocardItem';
import { Photocard } from '@/types/photocard';
import useToggle from '@/hooks/useToggle';
import { Loading } from '../Loading';
import { NothingCard } from '../NothingCard/NothingCard';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import PhotocardModal from '@/routes/photocard/[id]';
import useLocalStorage from '@/hooks/useLocalStorage';
import { useCart } from '@/hooks';
import { useLocation, useSearchParams } from 'react-router-dom';
import { validParams } from '@/utils/valid-params';
import useI18nText from '@/hooks/useI18nText';
import { FormattedMessage } from 'react-intl';
import { PhotocardsContext } from '@/routes/photocard/_components/Provider';
import { Image } from '../Image';
import { useWebViewRouter } from '@/hooks/useWebViewRouter';
import { trackEvent } from '../Event/_lib/eventTrack';

export function CardsList({ onClick }: { onClick: () => void }) {
  const { fetchApi } = useApi();
  const [searchParams] = useSearchParams();
  const [currency] = useLocalStorage('currency', null);
  const contentRef = useRef<HTMLDivElement>(null);
  const lastElement = useRef<HTMLDivElement>(null);
  const outOfStockModal = useToggle({ showing: false });
  const observerModal = useToggle({ showing: false });
  const { observe, unobserve } = useIntersectionObserver(() => observerModal.open());
  const { cartData } = useCart({});
  const [selected, setSelected] = useState<Photocard | null>(null);
  const queryClient = useQueryClient();
  const successAddCartModal = useToggle({ showing: false });
  const { group, member } = useContext(PhotocardsContext);
  const { direct } = useWebViewRouter();
  const location = useLocation();

  const category = searchParams.get('category') ?? null;
  const sort = searchParams.get('sort') ?? 'new';
  const groupIdx = searchParams.get('group') ?? String(group?.id) ?? '5';
  const memberIdx = searchParams.get('member') ?? String(member?.id) ?? '50';
  const q = searchParams.get('q') ?? '';

  const errorModalText1 = useI18nText('error_modal_desc_1');
  const errorModalText2 = useI18nText('error_modal_desc_2');

  const {
    data: cardsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ['card-list', groupIdx, memberIdx, category, sort, q],
    queryFn: ({ pageParam = undefined }) =>
      fetchApi((accessToken) =>
        getPhotocards({
          params: validParams({ category, sort, group: groupIdx, member: memberIdx, q, page: pageParam }),
          token: accessToken
        })
      ),
    getNextPageParam: (lastPage) => lastPage.next_page
  });

  const addCartMutation = useMutation({
    mutationFn: (offerId: number) => fetchApi((accessToken) => addToCart(offerId, accessToken)),
    mutationKey: ['cart'],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['card-list', groupIdx, memberIdx, category, sort, q] });
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      setSelected(null);

      successAddCartModal.open();
      setTimeout(() => {
        successAddCartModal.close();
      }, 600);
    },

    onError: (data: any) => {
      alert(`${errorModalText1}\n${errorModalText2}`);
    }
  });

  const moveTop = useCallback(() => {
    contentRef?.current?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [contentRef]);

  const cards = useMemo(() => {
    const results = cardsData ? cardsData.pages.flatMap(({ results }) => results) : [];
    results && results.length < 1 ? outOfStockModal.open() : outOfStockModal.close();
    if (isLoading) moveTop();
    return results;
  }, [cardsData, isLoading]);

  useEffect(() => {
    const getNext = async () => {
      if (observerModal.isShowing && !isFetchingNextPage && lastElement.current) {
        unobserve(lastElement.current);
        if (hasNextPage) {
          await fetchNextPage();
        }
        observerModal.close();
        observe(lastElement.current);
      }
    };
    getNext();
  }, [observerModal]);

  useEffect(() => {
    if (lastElement.current) {
      observe(lastElement.current);
    }
    return () => {
      if (lastElement.current) {
        unobserve(lastElement.current);
      }
    };
  }, []);

  const onResetSearchClick = () => {
    searchParams.delete('q');
    direct(`${location.pathname}?${searchParams}`);
  };

  const handlePhotocardItemClick = (photocard: Photocard, idx: number) => {
    if (isLoading) return;
    const line = Math.floor(idx / 7) + 1;
    setSelected(photocard);
    trackEvent('PLP_click_pc', { line });
  };

  return (
    <>
      <div
        className={`place-items-center relative ${
          q ? 'mt-[190px]' : 'mt-[135px]'
        }  bg-[#f9f9f9] w-full flex-1 grid grid-cols-7 gap-y-9 [grid-auto-rows:301px] p-5 pb-[150px]`}
        ref={contentRef}
        onClick={onClick}
      >
        {q && (
          <div
            className="right-0 w-[calc(100%-260px)] fixed flex justify-between px-4 py-3 top-[135px] bg-white h-[56px] items-center"
            onClick={onResetSearchClick}
          >
            <div>
              <span className="font-semibold">{cardsData?.pages[0].count ?? 0}</span>
              <span className="font-medium">{` results for `}</span>
              <span className="font-semibold">{`"${q}" ✨`}</span>
            </div>
            <div className="p-2 flex gap-1 text-[12px] font-medium border rounded-lg border-[#E0E0E0]">
              <Image alt={'cards-search-reset-icons'} width={16} src={'/icons/reset.svg'} className="rotate-30 fill-black" />
              Reset search
            </div>
          </div>
        )}

        {cards?.map((photocard, idx) => (
          <PhotocardItem
            key={`${photocard.id}-${idx}`}
            photocard={photocard}
            onClick={() => handlePhotocardItemClick(photocard, idx)}
          />
        ))}
        <div ref={lastElement} />

        {isLoading && <Loading className="absolute top-1/2 left-[47%] z-[99]" />}
        {!isLoading && isFetchingNextPage && !outOfStockModal.isShowing && <Loading className="relative left-[150%]" />}
        {!isLoading && !isFetchingNextPage && outOfStockModal.isShowing && <NothingCard />}
      </div>

      {selected && (
        <PhotocardModal
          {...selected}
          onAddToCart={(offerId) => {
            if (!cartData || cartData.cart_offers.length < 40) addCartMutation.mutate(offerId);
          }}
          onClose={() => setSelected(null)}
          currency={currency}
        />
      )}
      {successAddCartModal.isShowing && (
        <div className="w-[632px] h-[80px] rounded-[10px] flex justify-center items-center bg-[rgba(0,0,0,0.8)] fixed [top:calc(50%-40px)] [left:calc(50%-316px)] text-white text-[24px] leading-8 font-bold">
          <FormattedMessage id="added_to_cart" defaultMessage="The item has been added to your cart." />
        </div>
      )}
    </>
  );
}
